import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CardTitle from "@saleor/components/CardTitle";
import FormSpacer from "@saleor/components/FormSpacer";
import MutliTagAutoCompleteField, {
  MultiAutocompleteChoiceType
} from "@saleor/components/MutliTagAutoCompleteField";
import RichTextEditor from "@saleor/components/RichTextEditor";
import { ProductErrorFragment } from "@saleor/fragments/types/ProductErrorFragment";
import { commonMessages } from "@saleor/intl";
import { FetchMoreProps } from "@saleor/types";
import { getFormErrors, getProductErrorMessage } from "@saleor/utils/errors";
import { RawDraftContentState } from "draft-js";
import React, { useState } from "react";
import { useIntl } from "react-intl";

interface ProductDetailsFormProps {
  data: {
    brandLabel: string;
    description: RawDraftContentState;
    name: string;
    tags: MultiAutocompleteChoiceType[];
  };
  disabled?: boolean;
  isStoreAdmin?: boolean;
  tags?: MultiAutocompleteChoiceType[];
  errors: ProductErrorFragment[];
  validate: {
    name: null;
    description: null;
  };
  // Draftail isn't controlled - it needs only initial input
  // because it's autosaving on its own.
  // Ref https://github.com/mirumee/saleor/issues/4470
  initialDescription: RawDraftContentState;
  fetchMoreTags?: FetchMoreProps;
  fetchTags?: (query: string) => void;
  onChange(event: any);
}

const useStyles = makeStyles(
  () => ({
    description: {
      marginLeft: "11px"
    },
    descriptionLabel: {
      color: "#0030FF"
    }
  }),
  { name: "ProductOrganization" }
);

export const ProductDetailsForm: React.FC<ProductDetailsFormProps> = props => {
  const {
    data,
    disabled,
    errors,
    initialDescription,
    tags,
    onChange,
    isStoreAdmin,
    fetchMoreTags,
    fetchTags,
    validate
  } = props;
  const intl = useIntl();
  const formErrors = getFormErrors(["name", "descriptionJson"], errors);
  const classes = useStyles(props);

  const [tagText, setTagText] = useState("");

  const DisplayDescription =
    initialDescription && initialDescription.blocks
      ? initialDescription.blocks.map(e => <Typography>{e.text} </Typography>)
      : null;

  const handleChangeTag = e => {
    const getValue = e.target.value;
    const getAllTags = [...data.tags];
    const findTag = tags.filter(tag => tag.value === getValue)[0];
    const newEvent = {
      target: { name: "tags", value: [...getAllTags, findTag] }
    };
    const isDuplicate = getAllTags.find(
      currentTags => currentTags?.value === findTag?.value
    );
    if (!isDuplicate && findTag !== undefined) {
      onChange(newEvent);
    }
  };

  const handleRemoveTag = e => {
    const getValue = e.target.value;
    const getAllTags = [...data.tags];
    const newTags = getAllTags.filter(tag => tag.value !== getValue);
    const newEvent = { target: { name: "tags", value: newTags } };
    onChange(newEvent);
  };

  const handleTagAdd = () => {
    if (tagText !== "") {
      const findTag = tags.filter(tag => tag.label === tagText);
      const findSelectedTag = data.tags.filter(tag => tag.label === tagText);
      if (findTag.length === 0 && findSelectedTag.length === 0) {
        return {
          label: tagText,
          onClick: () => {
            const newEvent = {
              target: {
                name: "tags",
                value: [...data.tags, { label: tagText, value: tagText }]
              }
            };
            onChange(newEvent);
          },
          value: tagText
        };
      }
    }
    return null;
  };

  return (
    <Card>
      <CardTitle
        title={intl.formatMessage(commonMessages.productInformation)}
      />
      <CardContent>
        <TextField
          id="nameTxt"
          error={!!formErrors.name || !!validate?.name}
          helperText={
            getProductErrorMessage(formErrors.name, intl) || validate?.name
          }
          InputProps={{
            readOnly: disabled || isStoreAdmin
          }}
          fullWidth
          label={intl.formatMessage({
            defaultMessage: "Product Name",
            description: "product name"
          })}
          name="name"
          value={data.name || ""}
          onChange={onChange}
        />
        <FormSpacer />
        {isStoreAdmin ? (
          <div className={classes.description}>
            <Typography variant="body2" className={classes.descriptionLabel}>
              Description
            </Typography>
            <Typography variant="body1">{DisplayDescription}</Typography>
          </div>
        ) : (
          <RichTextEditor
            disabled={disabled}
            error={!!formErrors.descriptionJson || !!validate?.description}
            helperText={
              getProductErrorMessage(formErrors.descriptionJson, intl) ||
              validate?.description
            }
            initial={initialDescription}
            label={intl.formatMessage(commonMessages.description)}
            name="description"
            onChange={onChange}
          />
        )}
        <FormSpacer />
        <TextField
          id="brandTxt"
          InputProps={{
            readOnly: isStoreAdmin
          }}
          fullWidth
          label={intl.formatMessage({
            defaultMessage: "Brand",
            description: "Brand name"
          })}
          name="brandLabel"
          value={data.brandLabel || ""}
          onChange={onChange}
        />
        <FormSpacer />
        <MutliTagAutoCompleteField
          disabled={isStoreAdmin}
          add={handleTagAdd()}
          displayValues={data.tags}
          label={intl.formatMessage({
            defaultMessage: "Tag"
          })}
          choices={tags}
          limit={10}
          name="tags"
          value={data.tags.map(tag => tag?.value)}
          onChange={handleChangeTag}
          onChangeText={setTagText}
          onRemove={handleRemoveTag}
          fetchChoices={fetchTags}
          onFetchMore={fetchMoreTags?.onFetchMore}
          hasMore={fetchMoreTags.hasMore}
          loading={fetchMoreTags.loading}
        />
      </CardContent>
    </Card>
  );
};
export default ProductDetailsForm;
